import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface OpcaoCombo {
  value: string | number;
  label: string;
  /**
   * Aparece na lista em cinza e NAO pode ser escolhida. Existe porque sumir com a opcao faz o
   * operador concluir que ela **nao existe**: no combo de prateleiras, o lugar onde o produto ja
   * mora era filtrado fora, entao digitar o codigo dele trazia so os que casam por acidente ("A"
   * casa BROCAS, BALCAO, BANHEIRO...) e o unico item procurado era o unico ausente. Mostrar
   * desabilitado impede a duplicata do mesmo jeito e diz POR QUE.
   */
  desabilitada?: boolean;
  /** Texto curto a direita do rotulo dizendo o estado ("ja esta aqui"). So faz sentido com `desabilitada`. */
  nota?: string;
  /** Desenho a ESQUERDA do rotulo, so na lista aberta (ex.: a foto do produto). A caixa fechada
   *  mostra so o rotulo. */
  icone?: ReactNode;
}

interface ComboBoxProps {
  opcoes: OpcaoCombo[];
  value: string | number | null | undefined;
  onChange: (value: string | number | '') => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Foca o combo (fechado) ao montar — digitar uma letra ja abre a busca. */
  autoFocus?: boolean;
  /**
   * Leva o foco ao proximo campo do formulario: chamado depois de escolher (Enter ou clique), no
   * Tab da busca, e no Enter com o combo FECHADO e ja preenchido (senao o Enter so reabriria a
   * lista). Sem ele, Tab/Enter seguem o comportamento de sempre.
   */
  aoAvancar?: () => void;
  /** Largura MINIMA da lista aberta (px). Combo em coluna estreita (o "Da cotacao" da importacao)
   *  abre a lista mais larga que a caixa; sem ele a lista tem a largura da caixa, como sempre. */
  larguraMinLista?: number;
}

export function ComboBox({
  opcoes, value, onChange,
  placeholder = 'Selecione...',
  disabled = false,
  className = '',
  autoFocus = false,
  aoAvancar,
  larguraMinLista,
}: ComboBoxProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number; abrirCima: boolean } | null>(null);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  const selecionado = opcoes.find((o) => String(o.value) === String(value));
  const filtradas = busca
    ? opcoes.filter((o) => o.label.toLowerCase().includes(busca.toLowerCase()))
    : opcoes;

  // Primeiro item ESCOLHIVEL a partir de `inicio`, andando de `passo` em `passo`. -1 = nao ha.
  // As setas pulam o desabilitado em vez de parar nele — item que o teclado alcanca mas o Enter
  // recusa e a mesma armadilha do foco que nao se ve ([DA-305]).
  function acharAtivo(inicio: number, passo: number): number {
    for (let i = inicio; i >= 0 && i < filtradas.length; i += passo) {
      if (!filtradas[i]!.desabilitada) return i;
    }
    return -1;
  }

  // Reseta highlight quando filtra ou abre
  useEffect(() => {
    const p = acharAtivo(0, 1);
    setHighlight(p === -1 ? 0 : p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, aberto]);

  // Scroll do item destacado pra dentro da lista visivel
  useEffect(() => {
    if (!aberto || !painelRef.current) return;
    const item = painelRef.current.querySelector<HTMLElement>(`[data-combo-idx="${highlight}"]`);
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlight, aberto]);

  useEffect(() => {
    function fora(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (painelRef.current?.contains(target)) return;
      setAberto(false);
      setBusca('');
    }
    if (aberto) document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  useLayoutEffect(() => {
    if (!aberto || !containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    const alturaEstimada = 280;
    const espacoBaixo = window.innerHeight - r.bottom;
    const abrirCima = espacoBaixo < alturaEstimada && r.top > espacoBaixo;
    const width = Math.max(r.width, larguraMinLista ?? 0);
    setPos({
      top: abrirCima ? r.top - 4 : r.bottom + 4,
      // Lista mais larga que a caixa nao pode vazar pela direita da janela.
      left: Math.max(0, Math.min(r.left, window.innerWidth - width - 8)),
      width,
      abrirCima,
    });
  }, [aberto, larguraMinLista]);

  // Foco na busca SO depois que `pos` existe (o painel so renderiza com ele) — e em layout effect,
  // sincrono: se o foco atrasasse (setTimeout), as letras seguintes de quem digita rapido ainda
  // cairiam no container e se perderiam.
  useLayoutEffect(() => {
    if (aberto && pos) inputRef.current?.focus();
  }, [aberto, pos]);

  useEffect(() => {
    if (autoFocus && !disabled) containerRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function abrir(buscaInicial = '') {
    // O combo e' uma <div>, entao um `<fieldset disabled>` em volta (a Trava da ficha do produto)
    // desliga o botao de limpar mas NAO esta caixa: sem o `closest` ela abria e gravava a escolha
    // numa ficha em somente leitura (medido 09/10/2026 no combo Marca).
    if (disabled || containerRef.current?.closest('fieldset:disabled')) return;
    setAberto(true);
    setBusca(buscaInicial);
  }

  function selecionar(val: string | number) {
    onChange(val);
    setAberto(false);
    setBusca('');
    aoAvancar?.();
  }

  function limpar(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('');
    setBusca('');
  }

  return (
    <>
      {/* Fechado o combo e uma <div> — sem tabIndex o navegador nao a enxerga como parada de Tab e o
          foco PULA o campo (ia direto pro proximo botao). tabIndex + Enter/Espaco/seta abrindo poe o
          combo na ordem natural do formulario; abrir ja foca a busca (useLayoutEffect acima).
          Digitar uma letra tambem abre, ja levando o caractere pra busca: chegou no campo pelo Tab,
          escreve — sem clicar nem apertar Enter antes (como um <select> nativo se comporta). */}
      <div
        ref={containerRef}
        data-componente="005"
        onClick={() => abrir()}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={aberto}
        aria-haspopup="listbox"
        onKeyDown={(e) => {
          if (disabled || aberto) return;
          if (e.key === 'Enter' && aoAvancar && selecionado) { e.preventDefault(); aoAvancar(); }
          else if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); abrir(); }
          else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); abrir(e.key); }
        }}
        className={`combo-input ${aberto ? 'combo-input-ativo' : ''} ${disabled ? 'combo-input-disabled' : ''} ${autoFocus ? 'combo-input-autofoco' : ''} ${className}`}
      >
        <span className={selecionado ? '' : 'combo-placeholder'}>
          {selecionado ? selecionado.label : placeholder}
        </span>
        <div className="combo-icones">
          {selecionado && !disabled && (
            <button type="button" onClick={limpar} className="combo-limpar" aria-label="Limpar" tabIndex={aoAvancar ? -1 : undefined}>
              <X size={14} />
            </button>
          )}
          <ChevronDown size={14} className={`chev ${aberto ? 'chev-aberto' : ''}`} />
        </div>
      </div>

      {aberto && pos && (
        <div
          ref={painelRef}
          className="combo-painel"
          style={{
            position: 'fixed',
            top: pos.abrirCima ? undefined : pos.top,
            bottom: pos.abrirCima ? window.innerHeight - pos.top : undefined,
            left: pos.left,
            width: pos.width,
          }}
        >
          <div className="combo-busca">
            <input
              ref={inputRef}
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  setAberto(false);
                  setBusca('');
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const p = acharAtivo(highlight + 1, 1);
                  if (p !== -1) setHighlight(p);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  const p = acharAtivo(highlight - 1, -1);
                  if (p !== -1) setHighlight(p);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const item = filtradas[highlight];
                  if (item && !item.desabilitada) selecionar(item.value);
                } else if (e.key === 'Tab' && !e.shiftKey && aoAvancar) {
                  // Digitou e tabulou: escolhe o destacado (como o <select> nativo). Sem digitar,
                  // so fecha e mantem o valor que ja estava.
                  e.preventDefault();
                  const item = filtradas[highlight];
                  if (busca && item && !item.desabilitada) { selecionar(item.value); return; }
                  setAberto(false);
                  setBusca('');
                  aoAvancar();
                }
              }}
              placeholder="Digite pra buscar..."
            />
          </div>
          <div className="combo-lista">
            {filtradas.length === 0 ? (
              <div className="combo-vazio">Nenhum resultado</div>
            ) : (
              filtradas.map((o, idx) => (
                <div
                  key={o.value}
                  data-combo-idx={idx}
                  aria-disabled={o.desabilitada || undefined}
                  // O preventDefault vale mesmo no desabilitado: sem ele o clique tira o foco do
                  // campo de busca e quem digitou perde o lugar por ter esbarrado numa linha cinza.
                  onMouseDown={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    if (!o.desabilitada) selecionar(o.value);
                  }}
                  onMouseEnter={() => { if (!o.desabilitada) setHighlight(idx); }}
                  className={`combo-item ${o.desabilitada ? 'combo-item-desabilitado' : ''} ${!o.desabilitada && idx === highlight ? 'combo-item-highlight' : ''} ${String(o.value) === String(value) ? 'combo-item-ativo' : ''}`}
                >
                  {o.icone ? (
                    // Com icone (a foto do item da cotacao), icone e texto alinham pelo CENTRO — o
                    // `baseline` do .combo-item desalinharia a foto da linha.
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{ flexShrink: 0, display: 'flex' }}>{o.icone}</span>
                      <span>{o.label}</span>
                    </span>
                  ) : (
                    <span>{o.label}</span>
                  )}
                  {o.nota && <span className="combo-item-nota">{o.nota}</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
